import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { CodeBlock } from "@/components/CodeBlock";
import { AlertTriangle, CheckCircle2, Loader2, Radar, Stethoscope, XCircle, ZapOff } from "lucide-react";
import { toast } from "sonner";

function ProbePill({ ok, label, detail }) {
  const good = ok === true;
  const bad = ok === false;
  const bg = good ? "hsl(142 71% 15%)" : bad ? "hsl(0 72% 18%)" : "hsl(220 9% 16%)";
  const fg = good ? "hsl(142 71% 78%)" : bad ? "hsl(0 96% 82%)" : "hsl(215 20% 75%)";
  const border = good ? "hsl(142 71% 30%)" : bad ? "hsl(0 72% 40%)" : "hsl(220 9% 30%)";
  const Icon = good ? CheckCircle2 : bad ? XCircle : Loader2;
  return (
    <div className="flex items-center gap-2 rounded-md px-2.5 py-1.5 text-xs font-medium"
         style={{ background: bg, color: fg, border: `1px solid ${border}` }}>
      <Icon className={`h-3.5 w-3.5 ${ok === null || ok === undefined ? "animate-spin" : ""}`} />
      <span>{label}</span>
      {detail && <span className="opacity-70 font-mono">{detail}</span>}
    </div>
  );
}

export function SiteRescueBar({ site, onChanged }) {
  const [diagOpen, setDiagOpen] = useState(false);
  const [diagBusy, setDiagBusy] = useState(false);
  const [diag, setDiag] = useState(null);

  const [probeOpen, setProbeOpen] = useState(false);
  const [probeBusy, setProbeBusy] = useState(false);
  const [probe, setProbe] = useState(null);

  const [resetBusy, setResetBusy] = useState(false);
  const [resetLog, setResetLog] = useState([]);
  const [resetOpen, setResetOpen] = useState(false);

  if (!site.server_id) return null;

  const runDiagnose = async () => {
    setDiagBusy(true);
    setDiag(null);
    try {
      const { data } = await api.get(`/sites/${site.id}/diagnose`);
      setDiag(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "diagnose failed");
    } finally {
      setDiagBusy(false);
    }
  };

  const runProbe = async () => {
    setProbeBusy(true);
    setProbe(null);
    try {
      const { data } = await api.get(`/sites/${site.id}/probe`);
      setProbe(data);
      if (data?.public_ok) {
        toast.success(`Public URL is reachable (HTTP ${data.public_status})`);
      } else if (data?.loopback_ok && !data?.dns_ok) {
        toast.warning("Container is up, but DNS is not set. Add a wildcard A record.");
      } else if (data?.loopback_ok && data?.dns_ok && !data?.public_ok) {
        toast.warning("DNS + container OK, but public URL fails. Likely TLS or Caddy issue.");
      } else if (!data?.loopback_ok) {
        toast.error("Container is not responding on its loopback port. Redeploy or check logs.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "probe failed");
    } finally {
      setProbeBusy(false);
    }
  };

  const forceReset = async () => {
    setResetBusy(true);
    setResetLog([]);
    try {
      const { data } = await api.post(`/sites/${site.id}/force-reset`);
      setResetLog(data.log || []);
      toast.success("Force reset done — you can now redeploy");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "reset failed");
    } finally {
      setResetBusy(false);
    }
  };

  return (
    <Card
      data-testid="site-rescue-bar"
      className="mt-4 p-4 border-border"
      style={{ background: "hsl(35 92% 12%)", borderColor: "hsl(35 92% 30%)" }}
    >
      <div className="flex items-start gap-3 flex-wrap">
        <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" style={{ color: "hsl(43 96% 60%)" }} />
        <div className="flex-1 min-w-0">
          <div className="font-medium" style={{ color: "hsl(33 100% 92%)" }}>Rescue &amp; reachability</div>
          <div className="text-sm mt-0.5" style={{ color: "hsl(33 100% 82%)" }}>
            Subdomain not loading? Run <b>Probe</b> for a fast DNS/container/public URL check, or
            <b> Diagnose</b> for full Caddy + Docker logs from the VPS. Use <b>Force reset</b> to
            clear a stuck deploy.
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0 flex-wrap">
          <Dialog open={probeOpen} onOpenChange={(v) => { setProbeOpen(v); if (v && !probe) runProbe(); }}>
            <DialogTrigger asChild>
              <Button variant="secondary" size="sm" className="gap-1.5"
                      data-testid="site-rescue-probe-button">
                {probeBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Radar className="h-3.5 w-3.5" />}
                Probe
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle>Reachability probe — {site.name}</DialogTitle>
              </DialogHeader>
              {probeBusy && (
                <div className="py-8 text-center text-sm text-muted-foreground">Probing from your VPS…</div>
              )}
              {probe && (
                <div className="space-y-3">
                  <div className="text-sm">
                    Domain: <code className="font-mono">{probe.domain}</code>
                    {probe.host_port ? <> · loopback: <code className="font-mono">127.0.0.1:{probe.host_port}</code></> : null}
                  </div>
                  <div className="flex flex-col gap-2" data-testid="site-probe-results">
                    <ProbePill ok={probe.dns_ok} label="DNS resolves"
                               detail={probe.dns_result || "no record"} />
                    <ProbePill ok={probe.loopback_ok}
                               label="Container responds on loopback"
                               detail={probe.loopback_status ? `HTTP ${probe.loopback_status}` : "no port"} />
                    <ProbePill ok={probe.public_ok}
                               label="Public HTTPS URL"
                               detail={probe.public_status ? `HTTP ${probe.public_status}` : "unreachable"} />
                    {probe.cert_issuer && (
                      <ProbePill ok={!probe.cert_self_signed && !probe.cert_foreign_proxy}
                                 label={
                                   probe.cert_foreign_proxy
                                     ? "TLS cert is served by a foreign proxy (traffic is being intercepted)"
                                     : probe.cert_self_signed
                                       ? "TLS cert is self-signed (Caddy internal CA)"
                                       : "TLS cert issued by a public CA"
                                 }
                                 detail={probe.cert_issuer.replace(/^issuer=/, "").slice(0, 80)} />
                    )}
                    {probe.port_owner && (
                      <ProbePill ok={probe.port_owner === "caddy" && !probe.proxy_conflict}
                                 label={`Port 80/443 owner: ${probe.port_owner}`}
                                 detail={probe.proxy_conflict ? "traffic intercepted" : ""} />
                    )}
                    {probe.http_server_header !== undefined && probe.http_server_header !== "" && (
                      <ProbePill ok={probe.http_server_is_caddy}
                                 label={probe.http_server_is_caddy ? "HTTP is answered by Caddy" : "HTTP is answered by a foreign proxy"}
                                 detail={probe.http_server_header.replace(/^[Ss]erver:\s*/, "").slice(0, 80)} />
                    )}
                  </div>
                  {probe.provider_edge_proxy && (
                    <div className="rounded-md p-3 text-sm space-y-2"
                         style={{ background: "hsl(280 60% 12%)", color: "hsl(280 40% 92%)", border: "1px solid hsl(280 60% 40%)" }}>
                      <div>
                        <b>Your VPS provider is intercepting HTTP/HTTPS traffic — this is NOT a bug in your VPS or in emhost.</b>{" "}
                        Evidence: (1) Caddy on your VM answers correctly for local requests
                        <code className="mx-1 font-mono">Server: Caddy</code>, (2) no Docker/NAT
                        conflicts on the host, (3) your VM has a private IP{" "}
                        {probe.local_ips && <code className="font-mono">({probe.local_ips.split(/\s+/)[0]})</code>}{" "}
                        while its public IP is <code className="font-mono">{probe.public_ip}</code>,
                        (4) the &quot;public&quot; IP returns a foreign proxy&apos;s default cert / 404.
                        Something between the internet and your VM (a shared Traefik at the
                        hypervisor / provider edge) catches :80 / :443 before they reach you.
                      </div>
                      <div>
                        <b>You cannot fix this from inside your VPS.</b> You have two options:
                      </div>
                      <div>
                        <b>Option A — Contact your VPS provider ({probe.public_ip}):</b> ask them
                        to disable the shared HTTP reverse proxy for your VM, or to forward ports
                        80 &amp; 443 directly to your VM without their proxy layer.
                      </div>
                      <div>
                        <b>Option B — Use Cloudflare Tunnel</b> (bypasses the provider edge entirely,
                        outbound-only, no port issues, free plan works). Run these on your VPS Host Console:
                        <pre className="mt-1 font-mono text-[11px] whitespace-pre-wrap leading-snug"
                             style={{ background: "hsl(280 40% 8%)", padding: "8px", borderRadius: "6px" }}>
{`# 1) Install cloudflared on the VPS
curl -fsSL https://pkg.cloudflare.com/cloudflare-main.gpg | sudo gpg --dearmor -o /usr/share/keyrings/cloudflare-main.gpg
echo "deb [signed-by=/usr/share/keyrings/cloudflare-main.gpg] https://pkg.cloudflare.com/cloudflared \\$(lsb_release -cs) main" | sudo tee /etc/apt/sources.list.d/cloudflared.list
sudo apt-get update && sudo apt-get install -y cloudflared

# 2) Auth (opens a browser link — click it, log in, pick your zone/domain)
cloudflared tunnel login

# 3) Create a tunnel + route ${probe.domain} through it -> Caddy on 127.0.0.1:80
cloudflared tunnel create emhost
cloudflared tunnel route dns emhost ${probe.domain}
sudo mkdir -p /etc/cloudflared
sudo tee /etc/cloudflared/config.yml <<EOF
tunnel: emhost
credentials-file: /root/.cloudflared/<TUNNEL-UUID>.json
ingress:
  - hostname: ${probe.domain}
    service: http://127.0.0.1:80
  - service: http_status:404
EOF

# 4) Run as a service
sudo cloudflared service install
sudo systemctl start cloudflared

# Now ${probe.domain} routes via Cloudflare → your Caddy directly.`}
                        </pre>
                      </div>
                    </div>
                  )}
                  {probe.traffic_intercepted && probe.port_owner === "caddy" && !probe.provider_edge_proxy && (
                    <div className="rounded-md p-3 text-sm space-y-2"
                         style={{ background: "hsl(0 72% 14%)", color: "hsl(0 96% 92%)", border: "1px solid hsl(0 72% 40%)" }}>
                      <div>
                        <b>Caddy owns the port on the host, but external traffic is being intercepted
                        before it reaches Caddy.</b>{" "}This is almost always leftover
                        <b> Docker iptables NAT rules</b> from a previously-removed reverse-proxy
                        container (Traefik, nginx-proxy, etc). Docker&apos;s DOCKER chain rewrites
                        inbound :80/:443 packets to containers <i>before</i> the host listener sees them.
                      </div>
                      {probe.nat_leftovers && (
                        <div>
                          <b>Docker NAT rules currently targeting :80 / :443:</b>
                          <pre className="mt-1 font-mono text-[11px] whitespace-pre-wrap opacity-90">{probe.nat_leftovers}</pre>
                        </div>
                      )}
                      {probe.docker_proxies && (
                        <div>
                          <b>Docker containers still publishing :80 / :443:</b>
                          <pre className="mt-1 font-mono text-[11px] whitespace-pre-wrap opacity-90">{probe.docker_proxies}</pre>
                        </div>
                      )}
                      <div>
                        <b>Fix (run in your VPS Host Console):</b>
                        <pre className="mt-1 font-mono text-[11px] whitespace-pre-wrap leading-snug"
                             style={{ background: "hsl(0 40% 8%)", padding: "8px", borderRadius: "6px" }}>
{`# 1) List every container that STILL publishes :80 or :443 (running OR stopped)
docker ps -a --format '{{.Names}}|{{.Image}}|{{.Ports}}|{{.State}}' | grep -Ei ':80->|:443->'
# For every name that is NOT emhost-<something>-app / emhost-<something>-frontend:
docker stop <name> 2>/dev/null; docker rm <name>

# 2) Flush stale Docker NAT rules (safe: Docker rebuilds them at restart)
sudo iptables -t nat -F DOCKER 2>/dev/null || true

# 3) Restart Docker so it re-installs correct NAT rules for the remaining containers
sudo systemctl restart docker
sleep 5

# 4) Restart Caddy (Docker restart may briefly steal the port)
sudo systemctl restart caddy
sleep 3

# 5) Sanity check: this MUST return "Server: Caddy" (not Traefik / nginx / etc.)
curl -sSI http://${probe.domain}/ | grep -i server

# 6) Wait ~90 seconds, then re-run Probe. Public HTTPS should turn green.`}
                        </pre>
                      </div>
                    </div>
                  )}
                  {probe.proxy_conflict && probe.port_owner !== "caddy" && (
                    <div className="rounded-md p-3 text-sm space-y-2"
                         style={{ background: "hsl(0 72% 14%)", color: "hsl(0 96% 92%)", border: "1px solid hsl(0 72% 40%)" }}>
                      <div>
                        <b>Another reverse proxy is holding ports 80/443 on your VPS ({probe.port_owner}).</b>
                        {" "}This is why Let&apos;s Encrypt returns 404 on the ACME HTTP-01 challenge —
                        the request never reaches Caddy. You must stop &amp; remove it, then restart Caddy.
                      </div>
                      {probe.docker_proxies && (
                        <div>
                          <b>Docker containers publishing :80 or :443:</b>
                          <pre className="mt-1 font-mono text-xs opacity-90 whitespace-pre-wrap">{probe.docker_proxies}</pre>
                        </div>
                      )}
                      <div>
                        <b>Fix (run in your VPS Host Console):</b>
                        <pre className="mt-1 font-mono text-[11px] whitespace-pre-wrap leading-snug"
                             style={{ background: "hsl(0 40% 8%)", padding: "8px", borderRadius: "6px" }}>
{`# 1) Stop & disable Traefik (systemd install, if any)
sudo systemctl stop traefik 2>/dev/null; sudo systemctl disable traefik 2>/dev/null

# 2) Stop any Traefik / nginx / other-proxy Docker containers
docker ps --format '{{.Names}}|{{.Image}}|{{.Ports}}' | grep -Ei ':80->|:443->'
# Then, for each of the above container names:
docker stop <name> && docker rm <name>

# 3) Confirm ports are free (or only owned by caddy):
sudo ss -ltnp | grep -E ':(80|443)\\b'

# 4) Restart Caddy so it re-tries ACME:
sudo systemctl restart caddy && sleep 3 && sudo systemctl status caddy --no-pager

# 5) Re-run this Probe. Public HTTPS should turn green within ~30 seconds.`}
                        </pre>
                      </div>
                    </div>
                  )}
                  {!probe.dns_ok && (
                    <div className="rounded-md p-3 text-sm"
                         style={{ background: "hsl(35 92% 14%)", color: "hsl(33 100% 90%)", border: "1px solid hsl(35 92% 30%)" }}>
                      <b>DNS not set.</b> At your domain registrar, add a wildcard A record:<br />
                      <code className="font-mono text-xs">*.your-domain</code> → your VPS IP address.
                    </div>
                  )}
                  {probe.dns_ok && !probe.loopback_ok && (
                    <div className="rounded-md p-3 text-sm"
                         style={{ background: "hsl(0 72% 16%)", color: "hsl(0 96% 90%)", border: "1px solid hsl(0 72% 34%)" }}>
                      <b>Container is not responding.</b> Try <i>Redeploy</i>, then <i>Diagnose</i> to see docker logs.
                    </div>
                  )}
                  {probe.cert_self_signed && (
                    <div className="rounded-md p-3 text-sm"
                         style={{ background: "hsl(35 92% 14%)", color: "hsl(33 100% 90%)", border: "1px solid hsl(35 92% 30%)" }}>
                      <b>TLS certificate is self-signed (Caddy internal CA).</b> This is why the
                      browser shows <code>NET::ERR_CERT_AUTHORITY_INVALID</code>. Caddy could not
                      obtain a Let&apos;s Encrypt certificate. Most common causes:
                      <ul className="list-disc pl-5 mt-1 space-y-0.5">
                        <li>Port 80 (HTTP) blocked at the VPS provider firewall — Let&apos;s Encrypt HTTP-01 challenge needs it. Open port 80 in your VPS provider control panel (not just ufw).</li>
                        <li>Domain didn&apos;t resolve when Caddy first tried — after fixing DNS, run <i>Force reset</i> then <i>Deploy</i> to force a fresh ACME attempt.</li>
                        <li>Let&apos;s Encrypt rate-limit hit (5 failed attempts / hour). Wait ~1 hour and redeploy.</li>
                      </ul>
                      Run <i>Diagnose</i> and look at the <code>caddy journal</code> output — it will show the exact ACME error.
                    </div>
                  )}
                  {probe.dns_ok && probe.loopback_ok && !probe.public_ok && !probe.cert_self_signed && (
                    <div className="rounded-md p-3 text-sm"
                         style={{ background: "hsl(35 92% 14%)", color: "hsl(33 100% 90%)", border: "1px solid hsl(35 92% 30%)" }}>
                      <b>Caddy or upstream issue.</b> DNS is set and the container is alive, but the
                      public URL fails. Click <i>Regenerate configs</i> then <i>Deploy</i>, and
                      check <i>Diagnose</i> → caddy journal.
                    </div>
                  )}
                  {probe.error && <div className="text-red-400 text-sm">Error: {probe.error}</div>}
                </div>
              )}
              <DialogFooter>
                <Button variant="secondary" onClick={runProbe} disabled={probeBusy} className="gap-1.5">
                  {probeBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                  Re-run
                </Button>
                <Button variant="ghost" onClick={() => setProbeOpen(false)}>Close</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog open={diagOpen} onOpenChange={setDiagOpen}>
            <DialogTrigger asChild>
              <Button variant="secondary" size="sm" className="gap-1.5"
                      data-testid="site-rescue-diagnose-button"
                      onClick={runDiagnose}>
                {diagBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Stethoscope className="h-3.5 w-3.5" />}
                Diagnose
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-3xl">
              <DialogHeader>
                <DialogTitle>Diagnostics — {site.name}</DialogTitle>
              </DialogHeader>
              {!diag && diagBusy && <div className="py-8 text-center text-sm text-muted-foreground">Running diagnostics on the VPS…</div>}
              {diag && (
                <div className="space-y-3 max-h-[60vh] overflow-auto scroll-thin pr-1">
                  <div className="text-sm">
                    Target: <code className="font-mono">{diag.domain}</code> · project: <code className="font-mono">{diag.project}</code>
                    {diag.host_port ? <> · host port: <code className="font-mono">{diag.host_port}</code></> : null}
                  </div>
                  {(diag.checks || []).map((c, i) => (
                    <CodeBlock key={i}
                      label={`${c.label}  (exit=${c.exit_code})`}
                      code={(c.stdout || "") + (c.stderr ? "\n[stderr]\n" + c.stderr : "")}
                      maxHeight="200px" />
                  ))}
                  {diag.ssh_error && <div className="text-red-400 text-sm">SSH error: {diag.ssh_error}</div>}
                  {diag.error && <div className="text-red-400 text-sm">Error: {diag.error}</div>}
                </div>
              )}
              <DialogFooter>
                <Button variant="secondary" onClick={runDiagnose} disabled={diagBusy} className="gap-1.5">
                  {diagBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                  Re-run
                </Button>
                <Button variant="ghost" onClick={() => setDiagOpen(false)}>Close</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog open={resetOpen} onOpenChange={setResetOpen}>
            <DialogTrigger asChild>
              <Button size="sm" className="gap-1.5"
                      style={{ background: "hsl(0 72% 45%)", color: "white" }}
                      data-testid="site-rescue-force-reset-button">
                <ZapOff className="h-3.5 w-3.5" />
                Force reset
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-2xl">
              <DialogHeader>
                <DialogTitle>Force reset {site.name}</DialogTitle>
              </DialogHeader>
              <div className="text-sm text-muted-foreground">
                Kills any running build/container for this site on the VPS, tears down all containers
                for this project, and marks the site as &quot;failed&quot; so you can redeploy fresh.
                Files and settings are preserved.
              </div>
              {resetLog.length > 0 && (
                <div className="mt-3">
                  <CodeBlock label="force-reset log" code={resetLog.join("\n")} maxHeight="260px" />
                </div>
              )}
              <DialogFooter>
                <Button variant="ghost" onClick={() => setResetOpen(false)}>Close</Button>
                <Button onClick={forceReset} disabled={resetBusy}
                        style={{ background: "hsl(0 72% 45%)", color: "white" }}
                        className="gap-1.5"
                        data-testid="site-rescue-confirm-force-reset">
                  {resetBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ZapOff className="h-3.5 w-3.5" />}
                  Force reset
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </Card>
  );
}
